/**
 * PortalNotificationBell — sino de notificações do Portal do Parceiro (E21.1.5).
 *
 * Mostra o badge de não-lidas (polling ~30s) e um dropdown com as notificações
 * recentes (clicar numa marca como lida; botão "marcar todas"). As linhas são
 * escritas pelo Communication Hub quando um post da Central é publicado pra a
 * Audiência do parceiro.
 *
 * PR 4 Task 5 — clicar numa notificação com `link` fecha o menu e navega pra
 * lá. `link` já vem de uma lista fechada calculada no backend
 * (`link_da_notificacao`), mas só navegamos se `caminhoSeguroDoPortal`
 * aprovar o destino (mesma origem + dentro de `/portal/`, já resolvendo
 * dot-segments) — defesa em profundidade contra qualquer campo que atravessou
 * a rede.
 *
 * D3 (passo 19): o painel é o `PainelDoSino` do D1 com a fonte do portal; folha de baixo no celular; `ItemDoSinoDoPortal`.
 */
import { Bell } from "lucide-react";

import { InvolucroDoSino } from "@/components/notifications/InvolucroDoSino";
import {
  PainelDoSino,
  type FonteDoSino,
  type TextosDoSino,
} from "@/components/notifications/PainelDoSino";
import { ItemDoSinoDoPortal } from "@/components/portal/ItemDoSinoDoPortal";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  usePortalNotificationsList,
  usePortalUnreadCount,
  useMarkAllPortalNotificationsRead,
  useMarkPortalNotificationRead,
} from "@/hooks/usePortalNotifications";
import { caminhoSeguroDoPortal } from "@/lib/portal/caminhoSeguro";

/** Os textos do Início (`PainelDeNotificacoes.tsx:230-231`): mesmo portal, mesma frase. */
const TEXTOS_DO_SINO_DO_PORTAL: TextosDoSino = {
  carregando: "Carregando…",
  vazioTitulo: "Nenhuma notificação por enquanto.",
  vazioDetalhe: "Quando algo novo chegar para você, aparece aqui.",
  erroTitulo: "Não foi possível carregar as notificações.",
  tentarDeNovo: "Tentar de novo",
};

export function PortalNotificationBell() {
  // Celular = folha de baixo; computador = Popover. É decisão de polegar (768 px, o critério do cabeçalho interno), não de menu.
  const compacto = useIsMobile();
  const { data: naoLidas = 0 } = usePortalUnreadCount();
  const lista = usePortalNotificationsList(false, 20);
  const marcarLida = useMarkPortalNotificationRead();
  const marcarTodas = useMarkAllPortalNotificationsRead();

  const itens = lista.data?.items ?? [];

  const fonte: FonteDoSino = {
    notifications: itens,
    unreadCount: naoLidas,
    isLoading: lista.isLoading,
    isError: lista.isError,
    refetch: () => void lista.refetch(),
    // Já lida: nada a marcar (o clique só navega).
    markAsRead: (id) => {
      if (itens.some((n) => n.id === id && !n.is_read)) marcarLida.mutate(id);
    },
    // O botão antigo ficava `disabled` enquanto a marcação voava; o do painel do D1 não tem esse estado, então a guarda mora aqui.
    markAllRead: () => {
      if (!marcarTodas.isPending) marcarTodas.mutate();
    },
  };

  // 44 px e ícone de 20 px nas DUAS larguras (desenho §2.1/2.2). O `Button size="icon"` traz `sm:h-10 sm:w-10` e `sm:[&_svg]:size-4`, que o
  // `h-11 w-11` sozinho não vence a partir de 640 px (variantes diferentes: o tailwind-merge só resolve a MESMA); por isso o par `sm:` e o
  // `[&_svg]:size-5` no gatilho (um `h-5 w-5` no próprio ícone perde para o seletor de descendente `[&_svg]` do botão).
  const gatilho = (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-11 w-11 rounded-full text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 sm:h-11 sm:w-11 [&_svg]:size-5 sm:[&_svg]:size-5"
      aria-label={`Notificações${naoLidas > 0 ? ` (${naoLidas} não lidas)` : ""}`}
      aria-haspopup="dialog"
      data-testid="portal-notification-bell"
    >
      <Bell className="h-5 w-5" />
      {naoLidas > 0 && (
        <span
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-emerald-700 px-1 text-[10px] font-semibold text-white"
          data-testid="portal-notification-badge"
        >
          {naoLidas > 99 ? "99+" : naoLidas}
        </span>
      )}
    </Button>
  );

  return (
    <InvolucroDoSino
      gatilho={gatilho}
      compacto={compacto}
      idDeTesteDoPainel="portal-notification-panel"
    >
      {({ aoFechar, titulo }) => (
        <PainelDoSino
          titulo={titulo}
          aoFechar={aoFechar}
          compacto={compacto}
          fonte={fonte}
          hrefVerTodas="/portal/inicio#notificacoes"
          hrefPreferencias="/portal/preferencias"
          caminhoSeguro={caminhoSeguroDoPortal}
          Item={ItemDoSinoDoPortal}
          textos={TEXTOS_DO_SINO_DO_PORTAL}
          idsDeTeste={{
            marcarTodas: "portal-notification-markall",
            lista: "portal-notification-list",
          }}
        />
      )}
    </InvolucroDoSino>
  );
}

export default PortalNotificationBell;
